import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Dialog, Typography, TextField, Button, InputAdornment,
  IconButton, LinearProgress, CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ImageIcon from "@mui/icons-material/Image";
import { IMedia } from "../../../Api/Interface/api.interface";
import { useGetMediaQuery } from "../../../Api/attoDeskApi";
import { useUploads } from "../../../Components/Uploads/UploadContext";
import { useNotifier } from "../../../Core/Notifier";
import {
  NAVY, GOLD, GOLD_SOFT, NAVY_DARK, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";
import { formatBytes, formatDuration } from "./mediaUtils";

/**
 * Single-select media picker for a Video / Image zone. Reuses the shared
 * media list API (useGetMediaQuery) and the global upload queue
 * (useUploads) — the same endpoints the Media Library and Playlist editor
 * already use, so no new API surface is introduced.
 *
 * Type is LOCKED to the zone's widget kind (a video zone only ever shows
 * and uploads videos). Selecting an item — existing or freshly uploaded —
 * immediately returns it and closes the dialog.
 */

const PAGE_SIZE = 24;

const acceptFor = (kind: "image" | "video") =>
  kind === "video" ? ".mp4,video/mp4" : ".jpg,.jpeg,.png,.webp,image/*";

interface MediaPickerDialogProps {
  open: boolean;
  kind: "image" | "video";
  companyId?: number;
  /** When true, immediately open the OS file chooser on mount (Upload button). */
  initialUpload?: boolean;
  onClose: () => void;
  onSelect: (media: IMedia) => void;
}

const MediaPickerDialog: React.FC<MediaPickerDialogProps> = ({
  open, kind, companyId, initialUpload = false, onClose, onSelect,
}) => {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { showErrorMessage } = useNotifier();
  const { enqueue } = useUploads();

  const { data, isFetching, refetch } = useGetMediaQuery(
    { searchText: debounced, type: kind, company_id: companyId },
    { skip: !open },
  );

  const media: IMedia[] = useMemo(
    () => (Array.isArray(data?.data) ? ((data?.data as unknown) as IMedia[]) : []),
    [data],
  );
  const visible = media.slice(0, limit);

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset transient state whenever the dialog opens; kick off an upload
  // straight away when opened via the "Upload" button.
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setDebounced("");
    setLimit(PAGE_SIZE);
    setUploading(false);
    setUploadPct(0);
    if (initialUpload) {
      // Defer to let the hidden input mount.
      const t = setTimeout(() => fileInputRef.current?.click(), 0);
      return () => clearTimeout(t);
    }
  }, [open, initialUpload]);

  const doUpload = async (file: File) => {
    setUploading(true);
    setUploadPct(0);
    try {
      const result = await enqueue(file, companyId);
      const uploaded = (result?.data as unknown) as IMedia | undefined;
      if (uploaded && uploaded.id) {
        refetch();
        onSelect(uploaded);
      } else {
        showErrorMessage("Upload succeeded but no media was returned.");
        refetch();
      }
    } catch (err: any) {
      const msg = err?.message || "Upload failed";
      if (!msg.toLowerCase().includes("cancel")) showErrorMessage(msg);
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const validateAndUpload = (file: File | null | undefined) => {
    if (!file) return;
    const okType = kind === "video"
      ? file.type === "video/mp4" || file.name.toLowerCase().endsWith(".mp4")
      : /^image\/(jpeg|png|webp)$/.test(file.type) ||
        /\.(jpe?g|png|webp)$/i.test(file.name);
    if (!okType) {
      showErrorMessage(
        kind === "video"
          ? "Only MP4 videos are supported."
          : "Only JPG, PNG or WEBP images are supported.",
      );
      return;
    }
    // 500 MB — matches the backend UploadMediaRequest limit.
    if (file.size > 500 * 1024 * 1024) {
      showErrorMessage(`"${file.name}" is larger than the 500 MB limit.`);
      return;
    }
    void doUpload(file);
  };

  const KindIcon = kind === "video" ? OndemandVideoIcon : ImageIcon;

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { bgcolor: "#f8fafc", height: "80vh" } }}>
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
        px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5,
      }}>
        <KindIcon sx={{ color: GOLD, fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>
            {kind === "video" ? "Select a Video" : "Select an Image"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
            Choose from your library or upload a new file
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={uploading}
          sx={{ color: "rgba(255,255,255,0.8)" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Toolbar */}
      <Box sx={{ px: 3, py: 2, display: "flex", gap: 1.5, alignItems: "center" }}>
        <TextField size="small" placeholder="Search by file name…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300, bgcolor: "#fff" }} />
        <Box sx={{ flex: 1 }} />
        <input ref={fileInputRef} type="file" hidden accept={acceptFor(kind)}
          onChange={(e) => {
            validateAndUpload(e.target.files?.[0]);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }} />
        <Button variant="outlined" size="small" disabled={uploading}
          startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: "none", fontWeight: 700, borderColor: BORDER, color: NAVY }}>
          {kind === "video" ? "Upload Video" : "Upload Image"}
        </Button>
      </Box>

      {/* Upload progress banner */}
      {uploading && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 0.5 }}>
            Uploading…
          </Typography>
          <LinearProgress variant={uploadPct ? "determinate" : "indeterminate"} value={uploadPct} />
        </Box>
      )}

      {/* Body — drop target + grid */}
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          validateAndUpload(e.dataTransfer.files?.[0]);
        }}
        sx={{
          flex: 1, overflow: "auto", px: 3, pb: 3, position: "relative",
          outline: dragOver ? `2px dashed ${GOLD}` : "none",
          outlineOffset: -8,
          bgcolor: dragOver ? "rgba(212,175,55,0.06)" : "transparent",
        }}
      >
        {dragOver && (
          <Box sx={{
            position: "sticky", top: 0, zIndex: 2, textAlign: "center",
            py: 1, color: NAVY, fontWeight: 700, fontSize: 13,
          }}>
            Drop to upload
          </Box>
        )}

        {isFetching && media.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : media.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, color: TEXT_SECONDARY }}>
            <KindIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY }}>
              No {kind}s yet
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              Drag a file here or use the Upload button above.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: 1.5,
            }}>
              {visible.map((m) => (
                <MediaCard key={m.id} media={m} onSelect={() => onSelect(m)} />
              ))}
            </Box>
            {media.length > visible.length && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button size="small" onClick={() => setLimit((n) => n + PAGE_SIZE)}
                  sx={{ textTransform: "none", fontWeight: 700, color: NAVY }}>
                  Load more ({media.length - visible.length} more)
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  );
};

const MediaCard: React.FC<{ media: IMedia; onSelect: () => void }> = ({ media, onSelect }) => {
  const dur = formatDuration(media.duration);
  return (
    <Box
      onClick={onSelect}
      sx={{
        border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: "hidden",
        bgcolor: "#fff", cursor: "pointer", transition: "all .12s",
        "&:hover": { borderColor: GOLD, boxShadow: `0 2px 10px ${"rgba(212,175,55,0.3)"}`,
          "& .pick": { opacity: 1 } },
      }}
    >
      <Box sx={{ position: "relative", pt: "56%", bgcolor: "#0b1e2d" }}>
        {media.media_type === "video" ? (
          <Box component="video" src={media.url} muted preload="metadata"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Box component="img" src={media.url} alt={media.original_name}
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        )}
        <Box className="pick" sx={{
          position: "absolute", inset: 0, opacity: 0, transition: "opacity .12s",
          background: "rgba(11,30,45,0.45)", display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          <CheckCircleIcon sx={{ color: GOLD_SOFT, fontSize: 34 }} />
        </Box>
        {dur && (
          <Typography sx={{
            position: "absolute", bottom: 4, right: 4, fontSize: 10, fontWeight: 700,
            color: "#fff", bgcolor: "rgba(0,0,0,0.65)", px: 0.6, borderRadius: 0.5,
          }}>
            {dur}
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 1 }}>
        <Typography noWrap title={media.original_name}
          sx={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY }}>
          {media.original_name}
        </Typography>
        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
          {formatBytes(media.file_size)}
          {media.created_at ? ` · ${new Date(media.created_at).toLocaleDateString()}` : ""}
        </Typography>
      </Box>
    </Box>
  );
};

export default MediaPickerDialog;
